import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';

import { apiClient } from '../api/client';
import { useApi } from '../api/useApi';
import { AsyncBoundary } from '../components/AsyncState';
import { BackLink } from '../components/layout/BackLink';
import { RichText } from '../components/RichText';
import { paths } from '../routes';
import { formatDate } from '../utils/format';

export function ArticleDetailsPage(): ReactElement {
  const { t } = useTranslation();
  const { articleId } = useParams<{ articleId: string }>();
  const parsed = Number(articleId);
  const valid = Number.isInteger(parsed);
  // A non-numeric id redirects below before anything renders, so it must not go
  // out as a request for `/articles/NaN`.
  const article = useApi(
    () => (valid ? apiClient.getArticle(parsed) : new Promise<never>(() => undefined)),
    [parsed],
  );

  if (!valid) {
    return <Navigate to={paths.news} replace />;
  }

  // Only a resolved 404 means the article is gone; redirecting any earlier would
  // bounce away while the first request is still in flight.
  if (!article.loading && article.error?.status === 404) {
    return <Navigate to={paths.news} replace />;
  }

  return (
    <div className="pb-12">
      <div className="app-shell">
        <BackLink to={paths.news} label={t('articles.title')} />
      </div>

      <AsyncBoundary loading={article.loading} error={article.error} onRetry={article.reload}>
        {article.data !== undefined && (
          <>
            <div className="app-shell">
              <header className="mt-2 mb-5">
                {article.data.subtitle !== null && (
                  <p className="text-sm text-fog-500">{article.data.subtitle}</p>
                )}
                <h1 className="mt-1 text-3xl leading-tight font-bold text-fog-100">
                  {article.data.title}
                </h1>
                <p className="mt-3 text-sm text-fog-600">{formatDate(article.data.publishedAt)}</p>
              </header>
            </div>

            {article.data.imageUrl !== null && (
              <img
                src={article.data.imageUrl}
                alt=""
                className="aspect-[3/2] w-full object-cover"
              />
            )}

            <div className="app-shell mt-6">
              {/* Editorial HTML from the database; sanitised before injection. */}
              <RichText html={article.data.contentHtml ?? ''} className="text-sm text-fog-300" />
            </div>
          </>
        )}
      </AsyncBoundary>
    </div>
  );
}
